import { Build, Component, Element, Event, EventEmitter, h, Host, Listen, Prop, readTask, State, Watch, writeTask } from '@stencil/core';
import { DealerDefaultCountries, DealerFacets } from '~model/dealer/facets';
import { DealerResultItem } from '~model/dealer/result';
import { get, normalizeUrl } from '~service/ajax-service';
import { computeDistanceBetween } from '~service/google-maps/computeDistanceBetween';
import { Breakpoints, MobileUtility } from '~service/mobile-utility';
import { translate } from '~service/translation-service';
import LatLng = google.maps.LatLng;

interface Point {
  lat: number;
  lng: number;
}

@Component({
  tag: 'uvex-dealerlocator',
  styleUrl: 'dealerlocator.scss',
})
export class Dealerlocator {
  @Element() el: HTMLElement;

  @Prop({ attribute: 'default-center-point' })
  __defaultCenterPoint: string;

  get defaultCenterPoint(): Point | null {
    try {
      const defaultCenterPoint = JSON.parse(this.__defaultCenterPoint);
      return {
        lat: defaultCenterPoint.latitude,
        lng: defaultCenterPoint.longitude,
      };
    } catch (_) {
      return null;
    }
  }

  @Prop() defaultZoomLevel: number;

  @Prop({ attribute: 'default-countries' })
  __defaultCountries: string;

  get defaultCountries(): DealerDefaultCountries {
    try {
      return JSON.parse(this.__defaultCountries);
    } catch (_) {
      return {};
    }
  }

  @Event() hoverLocationChanged: EventEmitter<string>;
  @State() locations: DealerResultItem[] = [];
  @State() activeLocations: DealerResultItem[] = [];
  @State() activeLocation?: DealerResultItem = null;
  @State() hoverLocation?: DealerResultItem = null;
  @State() infoWindowLocation?: DealerResultItem = null;
  @State() showLocationDetails: DealerResultItem = null;
  @State() currentPosition?: Point = null;
  @State() activeFilters: { [key: string]: string } = {};
  @State() hideLocations = false;
  @State() facets: DealerFacets;
  @State() activeFacets: DealerFacets;
  @State() resultUrl = '';
  @State() resetUrl = '';
  @State() loading = false;
  @State() noResults = false;
  @State() searchInArea = false;
  @State() sw: LatLng = null;
  @State() ne: LatLng = null;
  @State() initialLoading = true;
  private _currentMapPosition?: Point;

  @Listen('click', { target: 'window', passive: true })
  onClick(event: MouseEvent) {
    const { target } = event;
    if ((target as HTMLElement).tagName.toLowerCase() !== 'button') {
      return;
    }
    if ((target as HTMLElement).dataset?.action !== 'location-show-more') {
      return;
    }

    const { location } = (target as HTMLElement).dataset;
    this.showLocationDetails = this.locations.find(_location => _location.id == location);
  }

  @Listen('facetChanged')
  async facetChangedHandler(event: CustomEvent) {
    this.resultUrl = event.detail.resultUrl;
    await this.fetchLocations();

    if (event.detail.changeActiveLocations) {
      this.changeActiveLocations();
    }
  }

  @Listen('facetResetAll')
  async facetResetAll() {
    this.resultUrl = '';
    this.ignoreCurrentViewport();
    await this.fetchLocations();
    this.changeActiveLocations();
  }

  @Listen('locationChanged')
  public locationChangedHandler(event: CustomEvent) {
    this.ignoreCurrentViewport();
    this.currentPosition = event.detail;
  }

  @Listen('activeLocationChanged')
  public activeLocationChangedHandler() {
    this.activeLocation = null;
  }

  @Listen('activeDetailsChanged')
  public activeDetailsChangedHandler(event: CustomEvent) {
    this.showLocationDetails = event.detail;
    if (MobileUtility.breakpoint === Breakpoints.TABLET) {
      this.hideLocations = false;
    }
  }

  @Listen('facetResetPreviousState')
  async facetResetPreviousStateHandler() {
    this.facets = this.activeFacets;
  }

  @Listen('changeActiveLocations')
  changeActiveLocations() {
    this.activeFacets = this.facets;
    if (this.currentPosition) {
      this.noResults = this.locations.length === 0;
      (this.el.querySelector('uvex-google-map') as HTMLUvexGoogleMapElement).clearMap();
      this.activeLocations = [];
      writeTask(() => {
        this.activeLocations = [...this.locations];
        readTask(() => (this.searchInArea = false));
      });
    }
  }

  @Listen('mapsMouseOver')
  onMapsMouseOver(event: CustomEvent<DealerResultItem>) {
    this.infoWindowLocation = event.detail;
  }

  @Listen('mapsMouseOut')
  onMapsMouseOut() {
    this.hoverLocation = null;
    this.infoWindowLocation = null;
  }

  @Listen('mapsClick')
  onMapsClick(event: CustomEvent<DealerResultItem>) {
    this.activeLocation = event.detail;

    if (this.showLocationDetails !== null) {
      this.showLocationDetails = this.activeLocation;
      return;
    }

    switch (MobileUtility.breakpoint) {
      case Breakpoints.TABLET:
        this.hideLocations = true;
        this.activeLocation = event.detail;
        break;
      case Breakpoints.DESKTOP:
      case Breakpoints.LARGE_DESKTOP:
        if (this.hideLocations === true) {
          setTimeout(() => this.scrollToLocation(event.detail.id), 200);
        } else {
          readTask(() => this.scrollToLocation(event.detail.id));
        }
        this.hideLocations = false;
        this.activeLocation = event.detail;
        break;
    }
  }

  @Listen('mapsInfoWindowClose')
  mapsInfoWindowClose(event: CustomEvent<DealerResultItem>) {
    if (this.infoWindowLocation?.id === event.detail.id) {
      this.infoWindowLocation = null;
      this.activeLocation = null;
    }
  }

  @Listen('mapsCenterChanged')
  onMapsCenterChanged(event: CustomEvent) {
    this._currentMapPosition = {
      lat: event.detail.lat(),
      lng: event.detail.lng(),
    };

    if (this.currentPosition) {
      const d = computeDistanceBetween(this.currentPosition.lat, this.currentPosition.lng, this._currentMapPosition.lat, this._currentMapPosition.lng);
      this.searchInArea = d > 10;
    }
  }

  @Watch('currentPosition')
  async currentPositionChangedHandler() {
    const activeElement = document.activeElement as HTMLInputElement;
    if (activeElement !== null) {
      activeElement.blur();
    }
    await this.fetchLocations();
    this.changeActiveLocations();
  }

  async componentWillLoad() {
    if (Build.isBrowser) {
      MobileUtility.addViewportChangedListener(breakpoint => (this.hideLocations = breakpoint === Breakpoints.MOBILE));
    }
    await this.fetchLocations();
  }

  toggleLocations() {
    this.hideLocations = !this.hideLocations;
  }

  render() {
    if (!Build.isBrowser) {
      return;
    }
    const mapSearchContainerProps = {
      class: {
        'maps-search-container': true,
        'small': this.activeLocation !== null,
        'details-open': this.showLocationDetails !== null && !this.hideLocations,
      },
    };

    const mapOptions = {
      center: this.defaultCenterPoint,
      zoom: this.defaultZoomLevel || 8,
      zoomControl: !this.hideLocations,
    };

    return (
      <Host class={this.hideLocations ? 'hide-locations' : ''}>
        <div {...mapSearchContainerProps}>
          {this.renderForm()}
          <div class="locations-container">
            {
              <uvex-google-map cluster={true} options={mapOptions}>
                {this.renderSearchInArea()}
                {this.renderActiveLocations()}
                {this.renderPins()}
              </uvex-google-map>
            }
            {this.renderLocations()}
            {this.renderEmptyLocations()}
            {this.showLocationDetails && <uvex-dealerlocator-location-detail location={this.showLocationDetails} />}
          </div>
        </div>
      </Host>
    );
  }

  private renderPins() {
    return this.activeLocations.map(location => {
      if (!location.subsidiary) {
        return (
          <uvex-dealerlocator-pin
            location={location}
            active={this.activeLocation === location || this.hoverLocation === location}
            showInfoWindow={this.infoWindowLocation === location}
            id={'dealerlocator-pin-' + location.id}
            hidden={true}
          />
        );
      }
    });
  }

  private renderForm() {
    let hasFilters = Object.keys(this.activeFacets || {}).length !== 0;

    return (
      <div class="maps-search-form">
        <h3>{translate('dealerlocator.filter.headline')}</h3>
        <div class="searchfield-wrap">
          <div class="form-group" id="places-search-container">
            <uvex-dealerlocator-suggester />
            <button
              type="button"
              id="dealer-loc_submit-search"
              class="btn-icon btn-search"
              onClick={_ => {
                this.el.querySelector('uvex-dealerlocator-suggester form').dispatchEvent(new CustomEvent('submit'));
              }}
            >
              <svg width="16" height="16" viewBox="0 0 1792 1792" xmlns="http://www.w3.org/2000/svg">
                <path d="M1216 832q0-185-131.5-316.5t-316.5-131.5-316.5 131.5-131.5 316.5 131.5 316.5 316.5 131.5 316.5-131.5 131.5-316.5zm512 832q0 52-38 90t-90 38q-54 0-90-38l-343-342q-179 124-399 124-143 0-273.5-55.5t-225-150-150-225-55.5-273.5 55.5-273.5 150-225 225-150 273.5-55.5 273.5 55.5 225 150 150 225 55.5 273.5q0 220-124 399l343 343q37 37 37 90z" />
              </svg>
            </button>
          </div>
          {hasFilters ? this.renderFilter() : null}
        </div>
        <button class="btn-icon btn-hide-search" onClick={_ => this.toggleLocations()}>
          <svg width="14" viewBox="0 0 11.015 18.868">
            <path d="M10.297 18.137 1.428 9.863 10.297.695" fill="none" stroke="#fff" stroke-width="2" />
          </svg>
        </button>
      </div>
    );
  }

  private renderFilter() {
    return <uvex-dealerlocator-filters facets={this.facets} resetUrl={this.resetUrl} defaultCountries={this.defaultCountries} />;
  }

  private renderActiveLocations() {
    if (this.activeLocation === null) {
      return;
    }

    return <uvex-dealerlocator-location location={this.activeLocation} />;
  }

  private renderSearchInArea() {
    if (!this.initialLoading || this.searchInArea) {
      return (
        <button id="dealer-loc_search-this-area" class="btn btn-area-search" onClick={_ => this.limitResultsToCurrentViewport()}>
          {translate('dealerlocator.areaSearch.button')}
        </button>
      );
    }
  }

  private renderLocations() {
    if (this.activeLocations.length === 0 || this.showLocationDetails !== null || this.hideLocations) {
      return;
    }

    return (
      <div class="locations-wrap">
        <div class="locations">
          {this.activeLocations.map(location => (
            <uvex-dealerlocator-location
              location={location}
              onClick={_ => {
                if (MobileUtility.breakpoint === Breakpoints.DESKTOP || MobileUtility.breakpoint === Breakpoints.LARGE_DESKTOP) {
                  this.activeLocation = location;
                }
              }}
              onMouseEnter={_ => {
                this.hoverLocation = location;
                this.hoverLocationChanged.emit('location-' + location.id);
                this.activeLocation = null;
                this.infoWindowLocation = null;
              }}
              onMouseLeave={_ => {
                this.hoverLocation = null;
                this.hoverLocationChanged.emit('location-' + location.id);
              }}
              class={{
                active: location === this.activeLocation || location === this.hoverLocation,
              }}
              id={'dealerlocator-location-' + location.id}
            />
          ))}
        </div>
      </div>
    );
  }

  private renderEmptyLocations() {
    if (this.currentPosition === null || this.noResults === false || this.resultUrl === '' || this.initialLoading) {
      return;
    }

    return (
      <div class="locations-wrap">
        <div class="empty-results">
          <div class="empty-header">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 37.646 38.756">
              <path fill="none" stroke="#252525" stroke-width="2" d="m22.97 24.08 13.969 13.969" />
              <g fill="none" stroke="#252525" stroke-width="2">
                <circle cx="13.799" cy="13.799" r="13.799" stroke="none" />
                <circle cx="13.799" cy="13.799" r="12.799" />
              </g>
              <g transform="translate(8.439 9)" fill="#fff" stroke="#252525">
                <circle cx="1" cy="1" r="1" stroke="none" />
                <circle cx="1" cy="1" r=".5" fill="none" />
              </g>
              <g transform="translate(16.439 9)" fill="#fff" stroke="#252525">
                <circle cx="1" cy="1" r="1" stroke="none" />
                <circle cx="1" cy="1" r=".5" fill="none" />
              </g>
              <path d="M7.939 18.5c2.058-3.541 8.379-3.541 11 0" fill="none" stroke="#252525" />
            </svg>
            <h3>{translate('dealerlocator.noresult.headline')}</h3>
          </div>
          <p>{translate('dealerlocator.noresult.text')}</p>
          <button class="btn btn-primary btn-block btn-xs" onClick={_ => this.facetResetAll()}>
            {translate('dealerlocator.noresult.reset')}
          </button>
        </div>
      </div>
    );
  }

  private async fetchLocations() {
    if (this.loading) {
      return;
    }

    this.loading = true;

    try {
      const { facets, result, search } = await get(this.getSearchUrl());
      this.facets = facets;
      if (!this.activeFacets) {
        this.activeFacets = facets;
      }
      this.locations = result.items;
      this.resetUrl = search.links.reset;
      this.activeLocation = this.infoWindowLocation = null;

      if (this.currentPosition !== null) {
        this.initialLoading = false;
      }
    } catch (e) {
    } finally {
      this.loading = false;
    }
  }

  private getSearchUrl() {
    let lastIndex = this.overrideCenterLocationInResultUrl();
    lastIndex = this.addGeoDistToResultUrl(lastIndex);
    lastIndex = this.addBoundingBoxToResultUrl(lastIndex);
    lastIndex = this.addCountryFilterToResultUrl(lastIndex);
    return this.resultUrl;
  }

  private overrideCenterLocationInResultUrl() {
    const url = new URL(normalizeUrl(this.resultUrl || ''));
    const position = this.currentPosition || this.defaultCenterPoint;
    let lastIndex = '';
    url.searchParams.forEach((item, index) => {
      if (item.includes('center_location:geodist')) {
        url.searchParams.set('tx_solr[resultsPerPage]', '12');
        url.searchParams.set(index, `center_location:geodist(${position.lat},${position.lng},20000)`);
      }
      if (item.includes('center_location:boundingbox') && this.sw !== null && this.ne !== null) {
        url.searchParams.set('tx_solr[resultsPerPage]', '2500');
        url.searchParams.set(index, `center_location:boundingbox(${this.sw.lat()},${this.sw.lng()},${this.ne.lat()},${this.ne.lng()})`);
      }
      if (item.includes('center_location:boundingbox') && this.sw === null && this.ne === null) {
        url.searchParams.delete(index);
      }
      lastIndex = index;
    });

    this.resultUrl = url.toString();
    return lastIndex;
  }

  private addGeoDistToResultUrl(lastIndex) {
    const url = new URL(normalizeUrl(this.resultUrl || ''));
    const position = this.currentPosition || this.defaultCenterPoint;

    if (this.currentPosition && url.search.indexOf('center_location%3Ageodist') === -1) {
      if (!url.searchParams.has('tx_solr[resultsPerPage]')) {
        url.searchParams.set('tx_solr[resultsPerPage]', '20');
      }

      let newIndex = 'tx_solr[filter][0]';
      if (lastIndex !== '' && lastIndex.indexOf('tx_solr[filter]') !== -1) {
        newIndex = Dealerlocator.getNewSearchIndex(lastIndex);
      }
      url.searchParams.set(newIndex, `center_location:geodist(${position.lat},${position.lng},20000)`);
      lastIndex = newIndex;
    }

    this.resultUrl = url.toString();
    return lastIndex;
  }

  private addBoundingBoxToResultUrl(lastIndex) {
    const url = new URL(normalizeUrl(this.resultUrl || ''));
    if (this.currentPosition && url.search.indexOf('center_location%3Aboundingbox') === -1 && this.sw !== null && this.ne !== null) {
      let newIndex = 'tx_solr[filter][1]';
      if (lastIndex !== '' && lastIndex.indexOf('tx_solr[filter]') !== -1) {
        newIndex = Dealerlocator.getNewSearchIndex(lastIndex);
      }
      url.searchParams.set('tx_solr[resultsPerPage]', '2500');
      url.searchParams.set(newIndex, `center_location:boundingbox(${this.sw.lat()},${this.sw.lng()},${this.ne.lat()},${this.ne.lng()})`);
      this.resultUrl = url.toString();
      return newIndex;
    }

    return lastIndex;
  }

  private addCountryFilterToResultUrl(lastIndex) {
    const url = new URL(normalizeUrl(this.resultUrl || ''));
    const formData = JSON.parse(localStorage.getItem('uvex.dealerlocator'));

    if (formData !== null && formData.country) {
      let newIndex = 'tx_solr[filter][1]';
      if (lastIndex !== '' && lastIndex.indexOf('tx_solr[filter]') !== -1) {
        newIndex = Dealerlocator.getNewSearchIndex(lastIndex);
      }
      const isoString = 'iso_country_code:' + formData.country;

      let hasCountryFilter = false;
      url.searchParams.forEach((item, index) => {
        if (item.includes('iso_country_code')) {
          hasCountryFilter = true;
          url.searchParams.set(index, isoString);
        }
      });

      if (!hasCountryFilter) {
        url.searchParams.set(newIndex, isoString);
      }
      this.resultUrl = url.toString();
      return newIndex;
    }

    return lastIndex;
  }

  private static getNewSearchIndex(lastIndex) {
    return 'tx_solr[filter][' + (parseInt(lastIndex.split('tx_solr[filter][')[1].substring(0, 1)) + 1) + ']';
  }

  private async limitResultsToCurrentViewport() {
    const $map = this.el.querySelector('uvex-google-map') as HTMLUvexGoogleMapElement;
    const currentBoundPosition = await $map.getBounds();
    this.sw = currentBoundPosition.getSouthWest();
    this.ne = currentBoundPosition.getNorthEast();
    this.currentPositionChangedHandler();
  }

  private ignoreCurrentViewport() {
    this.sw = null;
    this.ne = null;
  }

  private scrollToLocation(id: string) {
    const offsetTop = document.getElementById(`dealerlocator-location-${id}`)?.offsetTop || 0;
    const locations = this.el.querySelector('.locations');
    if (locations) {
      locations.scrollTop = offsetTop - 50;
    }
  }
}
